/**
 * Contexto de autenticación: loggedUser, login/logout, decodificación JWT, persistencia en localStorage y permisos por rol.
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import checkTimeStampLoggedUser from "utils/validation/checkTimestampLoggedUser";
import GETClientes from "helpers/clientes/GETClientes";
import {
  getDataInStorage,
  saveDataInStorage,
  removeDataInStorage,
} from "utils/storage/manageLocalStorage";
import GETListadoVistasRol from "helpers/rol/GETListadoVistasRol";
import GETListadoAccionesRol from "helpers/rol/GETListadoAccionesRol";
import { clearBrowserCache } from "utils/storage/clearBrowserCache";
import axiosController, { setLogoutFunc } from "helpers/axiosController";
import GETMenu from "helpers/rol/GETMenu";
import GETAlertaNotificaciones from "helpers/notificaciones/GETAlertaNotificaciones";
import { logger } from "utils/logger";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loggedUser, setLoggedUser] = useState({});
  const [decodedUser, setDecodedUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [listadoClientes, setListadoClientes] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState();
  const [vistasPorRol, setVistasPorRol] = useState({});
  const [accionesPorRol, setAccionesPorRol] = useState({});
  const [menuItemsRol, setMenuItemsRol] = useState([]);
  const [alertaNuevasNotis, setAlertaNuevasNotis] = useState(0);

  // Logout centralizado
  const logout = () => {
    removeDataInStorage("userPayload");
    setLoggedUser({});
    setDecodedUser({});
    setListadoClientes([]);
    setEmpresaSeleccionada(undefined);
    clearBrowserCache();
  };

  // Inyectamos logout al singleton de Axios
  useEffect(() => {
    setLogoutFunc(logout);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const response = await GETAlertaNotificaciones(loggedUser, empresaSeleccionada?.codCliente);
      if (response && response?.resultadoInterface?.resultado === 1) {
        const cantidadNoLeidas = response?.cantidadNoLeidas || [];
        setAlertaNuevasNotis(cantidadNoLeidas);
      }
    };
    fetchData();
  }, [empresaSeleccionada, loggedUser]);

  const login = async (userData) => {
    const { token } = userData;

    const responseMenuItems = await GETMenu(userData);

    if (responseMenuItems && responseMenuItems.resultadoInterface.resultado === 1) {
      setMenuItemsRol(responseMenuItems?.lista);
    }

    const responseListadoVistasRol = await GETListadoVistasRol(userData);
    if (responseListadoVistasRol && responseListadoVistasRol.resultadoInterface.resultado === 1) {
      setVistasPorRol(responseListadoVistasRol?.vistasPorRol);
    }

    const responseListadoAccionesRol = await GETListadoAccionesRol(userData);
    if (
      responseListadoAccionesRol &&
      responseListadoAccionesRol.resultadoInterface.resultado === 1
    ) {
      setAccionesPorRol(responseListadoAccionesRol?.accionesPorRol);
    }

    const decoded = jwtDecode(token);

    const role = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

    const decodedWithRole = {
      ...decoded,
      role,
      id: decoded?.userId,
      detalle: decoded?.username,
    };

    setLoggedUser(userData);
    setDecodedUser(decodedWithRole);
    saveDataInStorage("userPayload", userData);

    if (role === "Administrador") {
      const responseClientes = await GETClientes(userData);
      if (responseClientes?.resultadoInterface?.resultado === 1) {
        setListadoClientes(
          responseClientes?.clientes?.sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
        setEmpresaSeleccionada(responseClientes.clientes[0]);
      }
    } else {
      setEmpresaSeleccionada(decodedWithRole);
    }
  };

  // Carga inicial desde localStorage
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await getDataInStorage("userPayload");
        if (res?.token && checkTimeStampLoggedUser(jwtDecode(res.token).exp)) {
          await login(res);
        }
      } catch (error) {
        logger.error("Error al recuperar sesión:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loading,
        setLoading,
        loggedUser,
        decodedUser,
        listadoClientes,
        setListadoClientes,
        empresaSeleccionada,
        setEmpresaSeleccionada,
        vistasPorRol,
        accionesPorRol,
        alertaNuevasNotis,
        setAlertaNuevasNotis,
        login,
        logout,
        menuItemsRol, // Comparto el array de items de menu para armar Menu(layout) y las Rutas (AppRoute)
        axiosController, // se usa en helpers/componentes para hacerlo global
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
